import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore,
  type ReactNode
} from 'react'
import type { CommandResponse } from '../contract'
import type { CallHistoryEntry } from '../realtime/history'
import { TelephonyStore, type TelephonySnapshot } from './store'
import { MockSource } from '../realtime/mockSource'
import { SignalRSource } from '../realtime/signalrSource'
import type { ConnectionStatus, SendCommand, TelephonySource } from '../realtime/types'
import { apiBase, siteGuid, extension, getToken } from './auth'

interface TelephonyContextValue {
  store: TelephonyStore
  source: TelephonySource
  sendCommand: (cmd: SendCommand) => Promise<CommandResponse>
  station: { siteGuid: string; extension: string }
}

const Ctx = createContext<TelephonyContextValue | null>(null)

function createSource(): TelephonySource {
  // Live when an API base is configured (VITE_API_BASE or ?api=), else the mock.
  if (apiBase) {
    return new SignalRSource({ apiBase, siteGuid, extension, accessTokenFactory: getToken })
  }
  return new MockSource()
}

export function TelephonyProvider({ children }: { children: ReactNode }) {
  const station = useMemo(() => ({ siteGuid, extension }), [])
  const store = useMemo(() => new TelephonyStore(), [])
  const sourceRef = useRef<TelephonySource | null>(null)
  if (sourceRef.current === null) sourceRef.current = createSource()
  const source = sourceRef.current

  useEffect(() => {
    const off = source.onEvent((e) => store.apply(e))
    void source.start().catch((err) => console.warn('[stationbridge] source start failed:', err))
    return () => { off(); void source.stop() }
  }, [source, store])

  const value = useMemo<TelephonyContextValue>(
    () => ({ store, source, station, sendCommand: (cmd) => source.sendCommand(cmd) }),
    [store, source, station]
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

function useTelephony(): TelephonyContextValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useTelephony must be used within <TelephonyProvider>')
  return v
}

export function useSnapshot(): TelephonySnapshot {
  const { store } = useTelephony()
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
}

export function useSendCommand(): (cmd: SendCommand) => Promise<CommandResponse> {
  return useTelephony().sendCommand
}

export function useStation(): { siteGuid: string; extension: string } {
  return useTelephony().station
}

export function useConnectionStatus(): ConnectionStatus {
  const { source } = useTelephony()
  const [status, setStatus] = useState<ConnectionStatus>(source.status())
  useEffect(() => {
    setStatus(source.status())
    return source.onStatus(setStatus)
  }, [source])
  return status
}

export function useCallHistory(): { entries: CallHistoryEntry[] | null; refresh: () => Promise<void> } {
  const { source } = useTelephony()
  const [entries, setEntries] = useState<CallHistoryEntry[] | null>(null)
  const refresh = useCallback(async () => {
    try { setEntries(await source.fetchCallHistory()) } catch { setEntries([]) }
  }, [source])
  useEffect(() => { void refresh() }, [refresh])
  return { entries, refresh }
}
